/**
 * EventMasterWorkspace — UEIM-04
 * Full operational event planning workspace.
 * Route: /crm/events/:eid
 *
 * Sub-tabs: Overview | Scope | Functions & Streams | Timeline | Partners | Readiness & Issues | Documents | Communications | Event Graph (WVB-01)
 *
 * Truth boundaries:
 * - Scope Baseline is READ-ONLY (immutable after creation)
 * - Partner Assignments reference scope lines (one-to-many)
 * - No Supplier PO, no GL, no AP/AR
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin, Users, BarChart3, CheckSquare,
  AlertTriangle, Plus, ChevronDown, ChevronRight, Loader2,
  Clock, CheckCircle2, XCircle, Pencil, Trash2, UserPlus,
  Building2, Zap, FileText, Shield, TrendingUp, Radio, FolderOpen, Send,
  Network, Layers,
} from "lucide-react";
import { EventStageBar } from "@/components/EventStageBar";
import { WorkflowActionBar } from "@/components/WorkflowActionBar";
import { eventApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import EventDocumentWorkspace from "./EventDocumentWorkspace";
import EventCommsWorkspace from "./EventCommsWorkspace";
import EventGraphTab from "./EventGraphTab";
import EventFinancialsMiniPanel from "./EventFinancialsMiniPanel";

// ── Event lifecycle stages (for EventStageBar) ────────────────────────────────
const EVENT_STAGES = [
  { id: "PLANNING",  label: "Planning"  },
  { id: "CONFIRMED", label: "Confirmed" },
  { id: "LIVE",      label: "Live"      },
  { id: "COMPLETED", label: "Completed" },
  { id: "CLOSED",    label: "Closed"    },
];
const EVENT_TERMINAL = { id: "CANCELLED", label: "Cancelled" };

// ── Status helpers ─────────────────────────────────────────────────────────────
const EVENT_STATUS_STYLE = {
  PLANNING:  "bg-amber-100 text-amber-700 border-amber-200",
  CONFIRMED: "bg-indigo-100 text-indigo-700 border-indigo-200",
  LIVE:      "bg-emerald-100 text-emerald-700 border-emerald-200",
  COMPLETED: "bg-slate-100 text-slate-600",
  CLOSED:    "bg-slate-100 text-slate-400",
  CANCELLED: "bg-red-100 text-red-500",
};

const PRIORITY_COLOR = {
  LOW:      "text-slate-400",
  MEDIUM:   "text-amber-500",
  HIGH:     "text-orange-500",
  CRITICAL: "text-red-600",
};

const SEVERITY_STYLE = {
  INFO:     "bg-blue-50 text-blue-700 border-blue-100",
  WARNING:  "bg-amber-50 text-amber-700 border-amber-100",
  CRITICAL: "bg-red-50 text-red-700 border-red-100",
  BLOCKER:  "bg-red-100 text-red-800 border-red-200 font-semibold",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function StatusBadge({ status, map }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[status] || "bg-slate-100 text-slate-500"}`}>
      {status}
    </span>
  );
}

// ── Tabs ───────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "overview",        label: "Overview",           icon: BarChart3 },
  { id: "financials",      label: "Financials",         icon: TrendingUp },
  { id: "scope",           label: "Scope Baseline",     icon: FileText },
  { id: "functions",       label: "Functions & Streams",icon: Zap },
  { id: "timeline",        label: "Timeline",           icon: Clock },
  { id: "partners",        label: "Partners",           icon: Building2 },
  { id: "readiness",       label: "Readiness & Issues", icon: Shield },
  { id: "documents",       label: "Documents",          icon: FolderOpen },
  { id: "communications",  label: "Communications",     icon: Send },
  { id: "graph",           label: "Event Graph",        icon: Network },
];

export default function EventMasterWorkspace() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const canRead     = hasCapability(user, "event.read");
  const canManage   = hasCapability(user, "event.manage");
  const canApprove  = hasCapability(user, "event.approve");
  const canTeam     = hasCapability(user, "event.team.assign");
  const canOps      = hasCapability(user, "event.ops.manage");
  const canReadiness= hasCapability(user, "event.readiness.manage");
  const canOpsRead  = hasCapability(user, "event.ops.read");
  const canCloseout = hasCapability(user, "event.closeout.read");
  const canDocs     = hasCapability(user, "document.read");
  const canComms    = hasCapability(user, "comms.event.read");
  const canBudget   = hasCapability(user, "budget.view");
  const [transitioning, setTransitioning] = useState(false);

  const loadEvent = useCallback(async () => {
    try {
      const res = await eventApi.getEvent(eid);
      setEvent(res.data);
    } catch { toast.error("Failed to load event"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
    </div>
  );

  if (!event) return (
    <div className="min-h-screen flex items-center justify-center text-slate-500">
      Event not found.
    </div>
  );

  // ── Transition helpers ──────────────────────────────────────────────────────
  // Direct transitions allowed by backend:
  // PLANNING→CONFIRMED (requires event.approve), PLANNING→CANCELLED, CONFIRMED→CANCELLED
  // LIVE→COMPLETED (requires event.manage), LIVE→CANCELLED
  // CONFIRMED→LIVE and COMPLETED→CLOSED are BLOCKED — must use Ops / Closeout modules.
  const handleTransition = async (target) => {
    if (!window.confirm(`Transition event to ${target}?`)) return;
    setTransitioning(true);
    try {
      await eventApi.transition(eid, target);
      toast.success(`Event → ${target}`);
      await loadEvent();
    } catch (e) { toast.error(e.response?.data?.detail || "Transition failed"); }
    finally { setTransitioning(false); }
  };

  // Primary workflow action based on current status
  const primaryAction = (() => {
    switch (event.status) {
      case "PLANNING":
        if (!canApprove) return null;
        return {
          label: "Confirm Event",
          loading: transitioning,
          style: { background: "#4F46E5", color: "#fff" },
          onClick: () => handleTransition("CONFIRMED"),
        };
      case "CONFIRMED":
        if (!canOpsRead) return null;
        return {
          label: "Open Event Operations",
          style: { background: "#059669", color: "#fff" },
          onClick: () => navigate(`/crm/events/${eid}/operations`),
        };
      case "LIVE":
        if (!canManage) return null;
        return {
          label: "Mark Completed",
          loading: transitioning,
          style: { background: "#2563EB", color: "#fff" },
          onClick: () => handleTransition("COMPLETED"),
        };
      case "COMPLETED":
        if (!canCloseout) return null;
        return {
          label: "Open Event Closeout",
          style: { background: "#7C3AED", color: "#fff" },
          onClick: () => navigate(`/crm/events/${eid}/closeout`),
        };
      default:
        return null;
    }
  })();

  const cancelAction = !["COMPLETED","CLOSED","CANCELLED"].includes(event.status) && canManage
    ? { label: "Cancel Event", className: "text-red-600 border-red-200 hover:bg-red-50", onClick: () => handleTransition("CANCELLED") }
    : null;

  // ── Tab badges ──────────────────────────────────────────────────────────────
  const openIssues     = event.open_issues_count || 0;
  const noMilestones   = (event.milestones_count || 0) === 0;
  const noPartners     = (event.partners_count   || 0) === 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-bold text-slate-900 text-xl">{event.event_name}</h1>
                  <StatusBadge status={event.status} map={EVENT_STATUS_STYLE} />
                  <span className="text-xs text-slate-400">{event.creation_source === "LEGACY_CONFIRMED" ? "⚡ Legacy" : ""}</span>
                </div>
                <div className="flex items-center gap-4 mt-0.5 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{fmtDT(event.event_start_at)} — {fmtDT(event.event_end_at)}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.primary_venue}</span>
                  {event.timezone && <span className="text-slate-400">{event.timezone}</span>}
                </div>
              </div>
            </div>
            {/* Navigation shortcuts — not lifecycle transitions */}
            <div className="flex gap-2">
              {canCloseout && (event.status === "COMPLETED" || event.status === "CLOSED") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  onClick={() => navigate(`/crm/events/${eid}/closeout`)}
                  data-testid="open-event-closeout-btn"
                >
                  <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                  Event Closeout
                </Button>
              )}
              {canOpsRead && (event.status === "CONFIRMED" || event.status === "LIVE") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => navigate(`/crm/events/${eid}/operations`)}
                  data-testid="open-event-operations-btn"
                >
                  <Radio className="w-3.5 h-3.5 mr-1.5" />
                  Event Operations
                </Button>
              )}
            </div>
          </div>

          {/* Stage progress bar */}
          <div className="mt-3 border-t border-slate-100 pt-2">
            <EventStageBar
              stages={EVENT_STAGES}
              current={event.status}
              terminal={EVENT_TERMINAL}
              accentHex="#4F46E5"
            />
          </div>

          {/* Tab nav */}
          <div className="flex gap-1 mt-2 overflow-x-auto pb-0.5">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              // Badge: open issues on readiness tab; missing milestones on timeline
              const badge =
                t.id === "readiness" && openIssues > 0   ? openIssues :
                t.id === "timeline"  && noMilestones      ? "!" :
                t.id === "partners"  && noPartners && event.status === "CONFIRMED" ? "!" :
                null;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`workspace-tab-${t.id}`}
                  className={`relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all ${
                    active ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.label}
                  {badge !== null && (
                    <span
                      data-testid={`tab-badge-${t.id}`}
                      className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                      style={{ background: "#EF4444", color: "#fff" }}
                    >
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className={`max-w-7xl mx-auto px-6 py-6 ${primaryAction ? "pb-20" : ""}`}>
        {tab === "overview"   && <OverviewTab event={event} />}
        {tab === "financials" && canBudget && <FinancialsTab eid={eid} />}
        {tab === "financials" && !canBudget && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            You do not have access to Event Financials (budget.view required).
          </div>
        )}
        {tab === "scope"      && <ScopeTab eid={eid} />}
        {tab === "functions"  && <FunctionsTab eid={eid} canManage={canManage} canTeam={canTeam} />}
        {tab === "timeline"   && <TimelineTab eid={eid} canOps={canOps} />}
        {tab === "partners"   && <PartnersTab eid={eid} canManage={canManage} />}
        {tab === "readiness"  && <ReadinessTab eid={eid} canReadiness={canReadiness} />}
        {tab === "documents"  && canDocs && <DocumentsTab eid={eid} event={event} />}
        {tab === "documents"  && !canDocs && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            You do not have access to the Documents workspace.
          </div>
        )}
        {tab === "communications" && canComms && <CommsTab eid={eid} event={event} />}
        {tab === "communications" && !canComms && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            You do not have access to the Communications workspace.
          </div>
        )}
        {tab === "graph" && canRead && <EventGraphTab eid={eid} />}
        {tab === "graph" && !canRead && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            You do not have access to the Event Graph.
          </div>
        )}
      </div>

      {/* ── Workflow Action Bar ─────────────────────────────────────────────── */}
      <WorkflowActionBar
        visible={!!primaryAction}
        primary={primaryAction}
        secondary={cancelAction}
        hint={
          event.status === "PLANNING" ? "Ready to confirm? Ensure all scope, functions, and readiness gates are green." :
          event.status === "CONFIRMED" ? "Event confirmed. Start the Operations session to go live." :
          event.status === "LIVE"      ? "Event is live. Mark it completed once execution wraps up." :
          event.status === "COMPLETED" ? "Event completed. Begin the closeout process to formally close." :
          undefined
        }
      />
    </div>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab({ event }) {
  const navigate = useNavigate();
  const eid = event._id || event.id;
  const stats = [
    { label: "Functions",    value: event.functions_count ?? 0,   icon: Zap,         color: "text-indigo-600 bg-indigo-50" },
    { label: "Streams",      value: event.streams_count ?? 0,     icon: Users,       color: "text-purple-600 bg-purple-50" },
    { label: "Milestones",   value: event.milestones_count ?? 0,  icon: CheckSquare, color: "text-amber-600 bg-amber-50" },
    { label: "Tasks",        value: event.tasks_count ?? 0,       icon: CheckSquare, color: "text-teal-600 bg-teal-50" },
    { label: "Partners",     value: event.partners_count ?? 0,    icon: Building2,   color: "text-blue-600 bg-blue-50" },
    { label: "Open Issues",  value: event.open_issues_count ?? 0, icon: AlertTriangle, color: (event.open_issues_count ?? 0) > 0 ? "text-red-600 bg-red-50" : "text-slate-400 bg-slate-50" },
  ];

  const isOpsReady = event.status === "CONFIRMED" || event.status === "LIVE";
  const threadsActivated = event.threads_activated;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mx-auto mb-2`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* E2E-04D: Cross-navigation quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {isOpsReady && (
          <button
            onClick={() => navigate(`/crm/events/${eid}/operations`)}
            data-testid="cross-nav-operations"
            className="bg-white border border-emerald-200 rounded-xl p-4 text-left hover:border-emerald-400 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-2 mb-1">
              <Radio className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-slate-800">Event Operations</span>
            </div>
            <p className="text-xs text-slate-500">Control Tower · Run of Show · Go/No-Go · Incidents</p>
            <span className="text-[10px] text-emerald-600 font-mono mt-1 inline-flex items-center gap-0.5">Open Operations <ChevronRight className="w-3 h-3" /></span>
          </button>
        )}

        <button
          onClick={() => navigate(`/events/${eid}/threads`)}
          data-testid="cross-nav-threads"
          className={`bg-white border rounded-xl p-4 text-left hover:shadow-sm transition-all ${threadsActivated ? "border-indigo-200 hover:border-indigo-400" : "border-slate-200 hover:border-slate-400"}`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Layers className="w-4 h-4 text-indigo-500" />
            <span className="text-sm font-semibold text-slate-800">Event Threads</span>
            {threadsActivated && <span className="text-[9px] font-mono bg-indigo-50 text-indigo-600 border border-indigo-200 px-1.5 py-0.5 rounded">ACTIVE</span>}
          </div>
          <p className="text-xs text-slate-500">Customer · Finance · Operations · Service threads</p>
          <span className={`text-[10px] font-mono mt-1 inline-flex items-center gap-0.5 ${threadsActivated ? "text-indigo-600" : "text-slate-400"}`}>
            {threadsActivated ? <>Open Threads <ChevronRight className="w-3 h-3" /></> : "Activate Blueprint first"}
          </span>
        </button>

        {(event.status === "COMPLETED" || event.status === "CLOSED") && (
          <button
            onClick={() => navigate(`/crm/events/${eid}/closeout`)}
            data-testid="cross-nav-closeout"
            className="bg-white border border-blue-200 rounded-xl p-4 text-left hover:border-blue-400 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckSquare className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-800">Event Closeout</span>
            </div>
            <p className="text-xs text-slate-500">Post-event wrap-up, delivery sign-off, financial close</p>
            <span className="text-[10px] text-blue-600 font-mono mt-1 inline-flex items-center gap-0.5">Open Closeout <ChevronRight className="w-3 h-3" /></span>
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-3">Event Details</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          {[
            ["Event Name",    event.event_name],
            ["Status",        event.status],
            ["Start",         fmtDT(event.event_start_at)],
            ["End",           fmtDT(event.event_end_at)],
            ["Venue",         event.primary_venue],
            ["Timezone",      event.timezone],
            ["Source",        event.creation_source],
            ["Scope Lines",   event.scope_baseline?.line_count ?? "—"],
          ].map(([label, val]) => (
            <div key={label} className="flex gap-3">
              <span className="text-slate-400 w-28 shrink-0">{label}</span>
              <span className="font-medium text-slate-800">{val || "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Scope Tab ──────────────────────────────────────────────────────────────────
function ScopeTab({ eid }) {
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    eventApi.getScope(eid)
      .then(r => setScope(r.data))
      .catch(() => { /* ignore */ })
      .finally(() => setLoading(false));
  }, [eid]);

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!scope) return <div className="py-12 text-center text-slate-400">No scope baseline found.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3">
        <Shield className="w-4 h-4 text-indigo-500" />
        <p className="text-sm text-indigo-700">
          <strong>Immutable Baseline</strong> — This scope is derived from the accepted proposal and cannot be modified.
          Operational changes are tracked as issues.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="scope-baseline-table">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <p className="text-sm font-medium text-slate-700">{scope.lines?.length || 0} scope line(s)</p>
          <Badge className="text-xs border-0 bg-slate-200 text-slate-600">{scope.creation_source}</Badge>
        </div>
        {!scope.lines || scope.lines.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">No scope lines (created from LEGACY_CONFIRMED without proposal lines).</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead className="bg-slate-50 border-b border-slate-100 text-xs">
                <tr>
                  <th className="text-left px-5 py-2.5 font-medium text-slate-600 whitespace-nowrap">Description</th>
                  <th className="text-right px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap">Qty</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap">Unit</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600 whitespace-nowrap">Delivery Notes</th>
                  <th className="text-right px-4 py-2.5 font-medium text-slate-400 whitespace-nowrap">Baseline Price (ref)</th>
                </tr>
              </thead>
              <tbody>
                {scope.lines.map((line, i) => (
                  <tr key={line.id} className={`border-b border-slate-50 ${i % 2 === 1 ? "bg-slate-50/40" : ""}`} data-testid={`scope-line-${line.id}`}>
                    <td className="px-5 py-3 font-medium text-slate-800">{line.description}</td>
                    <td className="px-4 py-3 text-right">{line.quantity}</td>
                    <td className="px-4 py-3 text-slate-500">{line.unit || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{line.delivery_notes || "—"}</td>
                    <td className="px-4 py-3 text-right text-slate-400 text-xs">
                      {line.baseline_selling_price ? `₹${Number(line.baseline_selling_price).toLocaleString("en-IN")}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Functions & Streams Tab ────────────────────────────────────────────────────
function FunctionsTab({ eid, canManage, canTeam }) {
  const [functions, setFunctions] = useState([]);
  const [streams, setStreams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddFn, setShowAddFn] = useState(false);
  const [showAddStream, setShowAddStream] = useState(null); // function id to add stream to

  const load = useCallback(async () => {
    try {
      const [fns, sts] = await Promise.all([
        eventApi.listFunctions(eid),
        eventApi.listStreams(eid),
      ]);
      setFunctions(fns.data || []);
      setStreams(sts.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  // Load service categories for the function creation modal
  useEffect(() => {
    import("@/services/api").then(({ default: api }) =>
      api.get("/service-taxonomy/list").then(r => setCategories(r.data || []))
    ).catch(() => {});
  }, []);

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const streamsByFunction = (fid) => streams.filter(s => s.event_function_id === fid);

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setShowAddFn(true)} data-testid="add-function-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Function
          </Button>
        </div>
      )}

      {functions.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-xl border border-slate-200" data-testid="no-functions">
          <Zap className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">No event functions yet. Add a function to organise work streams.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {functions.map(fn => (
            <FunctionCard
              key={fn.id}
              fn={fn}
              streams={streamsByFunction(fn.id)}
              eid={eid}
              canManage={canManage}
              canTeam={canTeam}
              onAddStream={() => setShowAddStream(fn.id)}
              onStreamUpdated={load}
            />
          ))}
        </div>
      )}

      {showAddFn && (
        <AddFunctionModal
          eid={eid}
          categories={categories}
          onClose={() => setShowAddFn(false)}
          onAdded={() => { setShowAddFn(false); load(); }}
        />
      )}

      {showAddStream && (
        <AddStreamModal
          eid={eid}
          functionId={showAddStream}
          onClose={() => setShowAddStream(null)}
          onAdded={() => { setShowAddStream(null); load(); }}
        />
      )}
    </div>
  );
}

function FunctionCard({ fn, streams, eid, canManage, canTeam, onAddStream, onStreamUpdated }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`function-card-${fn.id}`}>
      <div
        className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <div>
            <div className="flex items-center gap-2">
              <p className="font-semibold text-slate-900">{fn.label}</p>
              <Badge className="text-xs border-0 bg-indigo-50 text-indigo-600">{fn.service_category_name}</Badge>
            </div>
            {(fn.function_venue || fn.function_start_at) && (
              <p className="text-xs text-slate-400 mt-0.5">
                {fn.function_venue && <span className="mr-3">{fn.function_venue}</span>}
                {fn.function_start_at && <span>{fmt(fn.function_start_at)}</span>}
              </p>
            )}
          </div>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); onAddStream(); }} data-testid={`add-stream-${fn.id}`}>
            <Plus className="w-3 h-3 mr-1" /> Stream
          </Button>
        )}
      </div>

      {expanded && (
        <div className="divide-y divide-slate-50">
          {streams.length === 0 ? (
            <div className="py-5 text-center text-slate-400 text-xs">No execution streams yet.</div>
          ) : (
            streams.map(s => (
              <StreamRow key={s.id} stream={s} eid={eid} canTeam={canTeam} canManage={canManage} onUpdated={onStreamUpdated} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function StreamRow({ stream, eid, canTeam, canManage, onUpdated }) {
  const STREAM_STATUS_STYLE = {
    NOT_STARTED: "bg-slate-100 text-slate-500",
    IN_PROGRESS: "bg-blue-100 text-blue-600",
    COMPLETED:   "bg-emerald-100 text-emerald-600",
    ON_HOLD:     "bg-amber-100 text-amber-600",
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await eventApi.updateStream(eid, stream.id, { status: newStatus });
      onUpdated();
    } catch { toast.error("Failed to update stream"); }
  };

  return (
    <div className="px-5 py-3 flex items-center justify-between flex-wrap gap-2" data-testid={`stream-row-${stream.id}`}>
      <div>
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-800 text-sm">{stream.name}</p>
          <Badge className={`text-xs border-0 ${STREAM_STATUS_STYLE[stream.status] || "bg-slate-100 text-slate-500"}`}>
            {stream.status.replace("_", " ")}
          </Badge>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">Lead: {stream.lead_user_id}</p>
      </div>
      {canManage && (
        <select
          value={stream.status}
          onChange={e => handleStatusChange(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2 py-1 text-slate-600 focus:outline-none"
          onClick={e => e.stopPropagation()}
        >
          {["NOT_STARTED", "IN_PROGRESS", "COMPLETED", "ON_HOLD"].map(s => (
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          ))}
        </select>
      )}
    </div>
  );
}

// ── Timeline Tab ───────────────────────────────────────────────────────────────
function TimelineTab({ eid, canOps }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [expandedMs, setExpandedMs] = useState({});
  const [showAddTask, setShowAddTask] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await eventApi.listMilestones(eid);
      setMilestones(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleTaskStatus = async (mid, tid, status) => {
    try {
      await eventApi.updateTask(eid, tid, { status });
      const res = await eventApi.listMilestones(eid);
      setMilestones(res.data || []);
    } catch { toast.error("Failed"); }
  };

  const handleMilestoneStatus = async (mid, status) => {
    try {
      await eventApi.updateMilestone(eid, mid, { status });
      load();
    } catch { toast.error("Failed"); }
  };

  const TASK_STATUS_COLOR = { TODO: "text-slate-400", IN_PROGRESS: "text-blue-500", DONE: "text-emerald-500", BLOCKED: "text-red-500" };

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4">
      {canOps && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-milestone-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Milestone
          </Button>
        </div>
      )}

      {milestones.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-xl border border-slate-200" data-testid="no-milestones">
          <Clock className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">No milestones yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {milestones.map(m => {
            const isExpanded = expandedMs[m.id] !== false;
            const done = m.status === "COMPLETED";
            return (
              <div key={m.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`milestone-${m.id}`}>
                <div
                  className={`px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${done ? "opacity-60" : ""}`}
                  onClick={() => setExpandedMs(s => ({ ...s, [m.id]: !isExpanded }))}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <CheckCircle2 className={`w-4 h-4 ${done ? "text-emerald-500" : "text-slate-300"}`} />
                    <div>
                      <p className={`font-semibold text-sm ${done ? "line-through text-slate-400" : "text-slate-900"}`}>{m.title}</p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                        <span>Due: {fmt(m.due_date)}</span>
                        <span className={PRIORITY_COLOR[m.priority]}>{m.priority}</span>
                        <span>{(m.tasks || []).length} task(s)</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    {canOps && (
                      <select
                        value={m.status}
                        onChange={e => { e.stopPropagation(); handleMilestoneStatus(m.id, e.target.value); }}
                        onClick={e => e.stopPropagation()}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none"
                      >
                        {["PENDING", "IN_PROGRESS", "COMPLETED", "MISSED"].map(s => <option key={s}>{s}</option>)}
                      </select>
                    )}
                    {canOps && (
                      <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setShowAddTask(m.id); }} data-testid={`add-task-${m.id}`}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {isExpanded && m.tasks?.length > 0 && (
                  <div className="border-t border-slate-50 divide-y divide-slate-50">
                    {m.tasks.map(t => (
                      <div key={t.id} className="px-10 py-2.5 flex items-center justify-between" data-testid={`task-${t.id}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${TASK_STATUS_COLOR[t.status]}`}>●</span>
                          <p className={`text-sm ${t.status === "DONE" ? "line-through text-slate-400" : "text-slate-700"}`}>{t.title}</p>
                          {t.due_date && <span className="text-xs text-slate-400">{fmt(t.due_date)}</span>}
                          <span className={`text-xs ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
                        </div>
                        {canOps && (
                          <select
                            value={t.status}
                            onChange={e => handleTaskStatus(m.id, t.id, e.target.value)}
                            className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none"
                          >
                            {["TODO", "IN_PROGRESS", "DONE", "BLOCKED"].map(s => <option key={s}>{s}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AddMilestoneModal eid={eid} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
      {showAddTask && <AddTaskModal eid={eid} milestoneId={showAddTask} onClose={() => setShowAddTask(null)} onAdded={() => { setShowAddTask(null); load(); }} />}
    </div>
  );
}

// ── Partners Tab ───────────────────────────────────────────────────────────────
function PartnersTab({ eid, canManage }) {
  const [partners, setPartners] = useState([]);
  const [scopeLines, setScopeLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        eventApi.listPartners(eid),
        eventApi.getScope(eid),
      ]);
      setPartners(pRes.data || []);
      setScopeLines(sRes.data?.lines || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (aid, status) => {
    try {
      await eventApi.updatePartner(eid, aid, { assignment_status: status });
      load();
    } catch { toast.error("Failed"); }
  };

  const STATUS_STYLE = {
    TENTATIVE:  "bg-amber-50 text-amber-700",
    CONFIRMED:  "bg-emerald-50 text-emerald-700",
    CANCELLED:  "bg-red-50 text-red-500",
  };

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-partner-btn" disabled={scopeLines.length === 0}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Assign Partner
          </Button>
        </div>
      )}

      {partners.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-xl border border-slate-200" data-testid="no-partners">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">No partner assignments yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-xs">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium text-slate-600">Partner</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Scope Item</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Allocation</th>
                <th className="text-left px-4 py-2.5 font-medium text-slate-600">Status</th>
                {canManage && <th className="px-4 py-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {partners.map(p => {
                const scopeLine = scopeLines.find(l => l.id === p.scope_line_id);
                return (
                  <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`partner-${p.id}`}>
                    <td className="px-5 py-3 font-medium text-slate-800">{p.partner_name}</td>
                    <td className="px-4 py-3 text-slate-600">{scopeLine?.description || p.scope_line_id}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {p.allocation_percent && <span>{p.allocation_percent}%</span>}
                      {p.allocation_quantity && <span> / Qty {p.allocation_quantity}</span>}
                      {p.responsibility_note && <span className="block text-slate-400">{p.responsibility_note}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`text-xs border-0 ${STATUS_STYLE[p.assignment_status] || "bg-slate-100 text-slate-500"}`}>
                        {p.assignment_status}
                      </Badge>
                    </td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <select
                          value={p.assignment_status}
                          onChange={e => handleStatusChange(p.id, e.target.value)}
                          className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none"
                        >
                          {["TENTATIVE", "CONFIRMED", "CANCELLED"].map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddPartnerModal
          eid={eid}
          scopeLines={scopeLines}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Readiness & Issues Tab ─────────────────────────────────────────────────────
function ReadinessTab({ eid, canReadiness }) {
  const [readiness, setReadiness] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddReadiness, setShowAddReadiness] = useState(false);
  const [showAddIssue, setShowAddIssue] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rRes, iRes] = await Promise.all([
        eventApi.listReadiness(eid),
        eventApi.listIssues(eid),
      ]);
      setReadiness(rRes.data || []);
      setIssues(iRes.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const handleReadinessStatus = async (rid, status) => {
    try { await eventApi.updateReadiness(eid, rid, { status }); load(); }
    catch { toast.error("Failed"); }
  };

  const handleIssueStatus = async (iid, status) => {
    try { await eventApi.updateIssue(eid, iid, { status }); load(); }
    catch { toast.error("Failed"); }
  };

  const READINESS_STATUS_STYLE = {
    OPEN:        "bg-red-50 text-red-600",
    IN_PROGRESS: "bg-amber-50 text-amber-600",
    READY:       "bg-emerald-50 text-emerald-700",
    WAIVED:      "bg-slate-100 text-slate-400",
  };
  const ISSUE_STATUS_STYLE = {
    OPEN:         "bg-red-100 text-red-700",
    INVESTIGATING:"bg-amber-100 text-amber-700",
    MITIGATING:   "bg-orange-100 text-orange-700",
    RESOLVED:     "bg-emerald-100 text-emerald-700",
    CLOSED:       "bg-slate-100 text-slate-400",
  };

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      {/* Readiness Items */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Readiness Checklist</h3>
          {canReadiness && (
            <Button size="sm" variant="outline" onClick={() => setShowAddReadiness(true)} data-testid="add-readiness-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
            </Button>
          )}
        </div>
        {readiness.length === 0 ? (
          <div className="py-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400 text-sm" data-testid="no-readiness">
            No readiness items yet.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-50">
            {readiness.map(r => (
              <div key={r.id} className="px-5 py-3 flex items-center justify-between flex-wrap gap-2" data-testid={`readiness-${r.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs border-0 ${READINESS_STATUS_STYLE[r.status] || "bg-slate-50 text-slate-500"}`}>{r.status}</Badge>
                    <p className="font-medium text-slate-800 text-sm">{r.title}</p>
                    <span className={`text-xs ${PRIORITY_COLOR[r.priority]}`}>{r.priority}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{r.category}</p>
                </div>
                {canReadiness && (
                  <select
                    value={r.status}
                    onChange={e => handleReadinessStatus(r.id, e.target.value)}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none"
                  >
                    {["OPEN", "IN_PROGRESS", "READY", "WAIVED"].map(s => <option key={s}>{s}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Issues */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Issues
            {issues.filter(i => ["OPEN", "INVESTIGATING", "MITIGATING"].includes(i.status)).length > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-600">
                {issues.filter(i => ["OPEN", "INVESTIGATING", "MITIGATING"].includes(i.status)).length} open
              </span>
            )}
          </h3>
          {canReadiness && (
            <Button size="sm" variant="outline" onClick={() => setShowAddIssue(true)} data-testid="add-issue-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Raise Issue
            </Button>
          )}
        </div>
        {issues.length === 0 ? (
          <div className="py-8 text-center bg-white rounded-xl border border-slate-200 text-slate-400 text-sm" data-testid="no-issues">
            No issues raised.
          </div>
        ) : (
          <div className="space-y-2">
            {issues.map(issue => (
              <div key={issue.id} className={`rounded-xl border px-5 py-3 ${SEVERITY_STYLE[issue.severity] || "bg-white border-slate-200"}`} data-testid={`issue-${issue.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs border-0 ${ISSUE_STATUS_STYLE[issue.status] || "bg-slate-100 text-slate-500"}`}>{issue.status}</Badge>
                      <p className="font-semibold text-sm">{issue.title}</p>
                      <span className="text-xs font-medium">[{issue.severity}]</span>
                    </div>
                    <p className="text-xs mt-1 opacity-80">{issue.description}</p>
                  </div>
                  {canReadiness && (
                    <select
                      value={issue.status}
                      onChange={e => handleIssueStatus(issue.id, e.target.value)}
                      className="text-xs border rounded-lg px-2 py-1 focus:outline-none bg-white"
                    >
                      {["OPEN", "INVESTIGATING", "MITIGATING", "RESOLVED", "CLOSED"].map(s => <option key={s}>{s}</option>)}
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddReadiness && (
        <AddReadinessModal eid={eid} onClose={() => setShowAddReadiness(false)} onAdded={() => { setShowAddReadiness(false); load(); }} />
      )}
      {showAddIssue && (
        <AddIssueModal eid={eid} onClose={() => setShowAddIssue(false)} onAdded={() => { setShowAddIssue(false); load(); }} />
      )}
    </div>
  );
}

// ── Inline Modals ──────────────────────────────────────────────────────────────

function ModalShell({ title, subtitle, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 overflow-y-auto max-h-[90vh]" data-testid={testId}>
        <h3 className="font-semibold text-slate-900 text-lg mb-1">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500 mb-4">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function AddFunctionModal({ eid, categories, onClose, onAdded }) {
  const [form, setForm] = useState({ service_category_id: "", label: "", function_venue: "", function_start_at: "", sequence_order: "1" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handle = async () => {
    if (!form.service_category_id) { toast.error("Select a service category"); return; }
    if (!form.label.trim()) { toast.error("Enter a function label"); return; }
    setSubmitting(true);
    try {
      await eventApi.createFunction(eid, {
        service_category_id: form.service_category_id,
        label: form.label,
        function_venue: form.function_venue || undefined,
        function_start_at: form.function_start_at || undefined,
        sequence_order: parseInt(form.sequence_order) || 1,
      });
      toast.success("Function added"); onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title="Add Event Function" onClose={onClose} testId="add-function-modal">
      <div className="space-y-3 mb-4">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Service Category (Taxonomy) <span className="text-red-400">*</span></Label>
          <select value={form.service_category_id} onChange={e => upd("service_category_id", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none" data-testid="fn-category-select">
            <option value="">— Select category —</option>
            {categories.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.name || c.display_name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Function Label <span className="text-red-400">*</span></Label>
          <Input value={form.label} onChange={e => upd("label", e.target.value)} placeholder="e.g. Main Stage & Entrance Decor" data-testid="fn-label-input" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Function Venue</Label>
            <Input value={form.function_venue} onChange={e => upd("function_venue", e.target.value)} placeholder="If different from event venue" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Function Start</Label>
            <Input type="datetime-local" value={form.function_start_at} onChange={e => upd("function_start_at", e.target.value)} />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handle} disabled={submitting} data-testid="add-fn-submit">{submitting ? "Adding…" : "Add Function"}</Button>
      </div>
    </ModalShell>
  );
}

function AddStreamModal({ eid, functionId, onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", description: "", lead_user_id: "" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handle = async () => {
    if (!form.name.trim()) { toast.error("Enter a stream name"); return; }
    if (!form.lead_user_id.trim()) { toast.error("Enter the stream lead"); return; }
    setSubmitting(true);
    try {
      await eventApi.createStream(eid, {
        event_function_id: functionId,
        name: form.name,
        description: form.description || undefined,
        lead_user_id: form.lead_user_id,
      });
      toast.success("Stream created"); onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title="Add Execution Stream" onClose={onClose} testId="add-stream-modal">
      <div className="space-y-3 mb-4">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Stream Name <span className="text-red-400">*</span></Label>
          <Input value={form.name} onChange={e => upd("name", e.target.value)} placeholder="e.g. Stage Setup & Teardown" data-testid="stream-name-input" />
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Description</Label>
          <Input value={form.description} onChange={e => upd("description", e.target.value)} placeholder="Scope of this stream" />
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Accountable Lead (User ID) <span className="text-red-400">*</span></Label>
          <Input value={form.lead_user_id} onChange={e => upd("lead_user_id", e.target.value)} placeholder="Lead user ID" data-testid="stream-lead-input" />
          <p className="text-xs text-slate-400 mt-0.5">One accountable lead required. Additional team members can be added after creation.</p>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handle} disabled={submitting} data-testid="add-stream-submit">{submitting ? "Creating…" : "Create Stream"}</Button>
      </div>
    </ModalShell>
  );
}

function AddMilestoneModal({ eid, onClose, onAdded }) {
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "MEDIUM" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handle = async () => {
    if (!form.title.trim()) { toast.error("Enter a milestone title"); return; }
    if (!form.due_date) { toast.error("Enter a due date"); return; }
    setSubmitting(true);
    try {
      await eventApi.createMilestone(eid, {
        title: form.title,
        description: form.description || undefined,
        due_date: form.due_date,
        priority: form.priority,
      });
      toast.success("Milestone created"); onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title="Add Milestone" onClose={onClose} testId="add-milestone-modal">
      <div className="space-y-3 mb-4">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Title <span className="text-red-400">*</span></Label>
          <Input value={form.title} onChange={e => upd("title", e.target.value)} placeholder="e.g. Venue Booking Confirmed" data-testid="milestone-title" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Due Date <span className="text-red-400">*</span></Label>
            <Input type="date" value={form.due_date} onChange={e => upd("due_date", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Priority</Label>
            <select value={form.priority} onChange={e => upd("priority", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
              {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handle} disabled={submitting} data-testid="add-milestone-submit">{submitting ? "Creating…" : "Add Milestone"}</Button>
      </div>
    </ModalShell>
  );
}

function AddTaskModal({ eid, milestoneId, onClose, onAdded }) {
  const [form, setForm] = useState({ title: "", due_date: "", priority: "MEDIUM" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handle = async () => {
    if (!form.title.trim()) { toast.error("Enter a task title"); return; }
    setSubmitting(true);
    try {
      await eventApi.createTask(eid, {
        milestone_id: milestoneId,
        title: form.title,
        due_date: form.due_date || undefined,
        priority: form.priority,
      });
      toast.success("Task added"); onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title="Add Task" onClose={onClose} testId="add-task-modal">
      <div className="space-y-3 mb-4">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Task Title <span className="text-red-400">*</span></Label>
          <Input value={form.title} onChange={e => upd("title", e.target.value)} placeholder="e.g. Send venue confirmation email" data-testid="task-title" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Due Date</Label>
            <Input type="date" value={form.due_date} onChange={e => upd("due_date", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Priority</Label>
            <select value={form.priority} onChange={e => upd("priority", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
              {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handle} disabled={submitting} data-testid="add-task-submit">{submitting ? "Adding…" : "Add Task"}</Button>
      </div>
    </ModalShell>
  );
}

function AddPartnerModal({ eid, scopeLines, onClose, onAdded }) {
  const [form, setForm] = useState({ scope_line_id: "", partner_name: "", allocation_percent: "", allocation_quantity: "", responsibility_note: "", assignment_status: "TENTATIVE" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handle = async () => {
    if (!form.scope_line_id) { toast.error("Select a scope line"); return; }
    if (!form.partner_name.trim()) { toast.error("Enter partner name"); return; }
    setSubmitting(true);
    try {
      await eventApi.createPartner(eid, {
        scope_line_id: form.scope_line_id,
        partner_name: form.partner_name,
        allocation_percent: form.allocation_percent ? parseFloat(form.allocation_percent) : undefined,
        allocation_quantity: form.allocation_quantity ? parseFloat(form.allocation_quantity) : undefined,
        responsibility_note: form.responsibility_note || undefined,
        assignment_status: form.assignment_status,
      });
      toast.success("Partner assigned"); onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title="Assign Partner" subtitle="One scope line may have multiple partner assignments." onClose={onClose} testId="add-partner-modal">
      <div className="space-y-3 mb-4">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Scope Line <span className="text-red-400">*</span></Label>
          <select value={form.scope_line_id} onChange={e => upd("scope_line_id", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none" data-testid="partner-scope-select">
            <option value="">— Select scope line —</option>
            {scopeLines.map(l => <option key={l.id} value={l.id}>{l.description} (qty {l.quantity})</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Partner Name <span className="text-red-400">*</span></Label>
          <Input value={form.partner_name} onChange={e => upd("partner_name", e.target.value)} placeholder="e.g. Golden Arch Decor" data-testid="partner-name-input" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Allocation %</Label>
            <Input type="number" value={form.allocation_percent} onChange={e => upd("allocation_percent", e.target.value)} placeholder="e.g. 50" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Qty</Label>
            <Input type="number" value={form.allocation_quantity} onChange={e => upd("allocation_quantity", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
            <select value={form.assignment_status} onChange={e => upd("assignment_status", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
              {["TENTATIVE", "CONFIRMED"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Responsibility Note</Label>
          <Input value={form.responsibility_note} onChange={e => upd("responsibility_note", e.target.value)} placeholder="e.g. Handles Stage 1 only" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handle} disabled={submitting} data-testid="add-partner-submit">{submitting ? "Assigning…" : "Assign Partner"}</Button>
      </div>
    </ModalShell>
  );
}

function AddReadinessModal({ eid, onClose, onAdded }) {
  const [form, setForm] = useState({ category: "", title: "", description: "", priority: "MEDIUM" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handle = async () => {
    if (!form.title.trim()) { toast.error("Enter a title"); return; }
    if (!form.category.trim()) { toast.error("Enter a category"); return; }
    setSubmitting(true);
    try {
      await eventApi.createReadiness(eid, { category: form.category, title: form.title, description: form.description || undefined, priority: form.priority });
      toast.success("Readiness item added"); onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title="Add Readiness Item" onClose={onClose} testId="add-readiness-modal">
      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Category <span className="text-red-400">*</span></Label>
            <Input value={form.category} onChange={e => upd("category", e.target.value)} placeholder="e.g. Logistics, Permits, Crew" data-testid="readiness-category" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Priority</Label>
            <select value={form.priority} onChange={e => upd("priority", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none">
              {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Title <span className="text-red-400">*</span></Label>
          <Input value={form.title} onChange={e => upd("title", e.target.value)} placeholder="e.g. Venue access permit obtained" data-testid="readiness-title" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handle} disabled={submitting} data-testid="add-readiness-submit">{submitting ? "Adding…" : "Add Item"}</Button>
      </div>
    </ModalShell>
  );
}

function AddIssueModal({ eid, onClose, onAdded }) {
  const [form, setForm] = useState({ title: "", description: "", severity: "WARNING" });
  const [submitting, setSubmitting] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handle = async () => {
    if (!form.title.trim()) { toast.error("Enter a title"); return; }
    if (!form.description.trim()) { toast.error("Enter a description"); return; }
    setSubmitting(true);
    try {
      await eventApi.createIssue(eid, { title: form.title, description: form.description, severity: form.severity });
      toast.success("Issue raised"); onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title="Raise Issue" onClose={onClose} testId="add-issue-modal">
      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Title <span className="text-red-400">*</span></Label>
            <Input value={form.title} onChange={e => upd("title", e.target.value)} placeholder="e.g. Vendor not confirmed" data-testid="issue-title" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Severity</Label>
            <select value={form.severity} onChange={e => upd("severity", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none" data-testid="issue-severity">
              {["INFO", "WARNING", "CRITICAL", "BLOCKER"].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Description <span className="text-red-400">*</span></Label>
          <Textarea value={form.description} onChange={e => upd("description", e.target.value)} rows={3} placeholder="Describe the issue and its impact" data-testid="issue-description" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handle} disabled={submitting} data-testid="add-issue-submit">{submitting ? "Raising…" : "Raise Issue"}</Button>
      </div>
    </ModalShell>
  );
}


// ── DocumentsTab (DCC-01) ─────────────────────────────────────────────────────

function DocumentsTab({ eid, event }) {
  return <EventDocumentWorkspace eid={eid} event={event} embedded />;
}

// ── CommsTab (DCC-02) ─────────────────────────────────────────────────────────

function CommsTab({ eid, event }) {
  return <EventCommsWorkspace eid={eid} event={event} embedded />;
}

// ── FinancialsTab (WVB-02) ────────────────────────────────────────────────────

function FinancialsTab({ eid }) {
  return <EventFinancialsMiniPanel eid={eid} />;
}
